import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/CardTransition.scss';

const CardTransitionContext = createContext(null);

export const useCardTransition = () => useContext(CardTransitionContext);

const EXPAND_MS = 500;
const COLLAPSE_MS = 450;
const FADE_MS = 250;
const EASE = 'cubic-bezier(0.32,0.72,0,1)';

export const CardTransitionProvider = ({ children }) => {
    const navigate = useNavigate();
    const [media, setMedia] = useState(null);
    const phaseRef = useRef('idle');
    const cloneRef = useRef(null);
    const overlayRef = useRef(null);
    const cardRectsRef = useRef({});
    const mediaCache = useRef({});

    const getHeroRect = () => ({
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: Math.min(window.innerHeight * 0.7, 700),
    });

    const applyRect = (el, rect, radius, extra = {}) => {
        Object.assign(el.style, {
            top: rect.top + 'px',
            left: rect.left + 'px',
            width: rect.width + 'px',
            height: rect.height + 'px',
            borderRadius: radius,
            ...extra,
        });
    };

    const runExpand = useCallback((rect, mediaData, targetUrl) => {
        const clone = cloneRef.current;
        const overlay = overlayRef.current;
        if (!clone || !overlay) return;

        const hero = getHeroRect();

        // Overlay instant full opacity — blocks everything underneath
        overlay.style.transition = 'none';
        overlay.style.opacity = '1';
        overlay.style.display = 'block';

        // Clone at card position
        clone.style.transition = 'none';
        clone.style.opacity = '1';
        clone.style.display = 'block';
        applyRect(clone, rect, '20px');

        void clone.offsetHeight;

        // Animate clone to hero position
        clone.style.transition = `top ${EXPAND_MS}ms ${EASE}, left ${EXPAND_MS}ms ${EASE}, width ${EXPAND_MS}ms ${EASE}, height ${EXPAND_MS}ms ${EASE}, border-radius ${EXPAND_MS}ms ${EASE}`;
        applyRect(clone, hero, '0px');

        // Navigate after animation — overlay hides everything so no flash
        setTimeout(() => {
            navigate(targetUrl, { state: { fromCard: true } });

            // Wait for feature page to render, then fade out both
            setTimeout(() => {
                requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (clone) {
                        clone.style.transition = `opacity ${FADE_MS}ms ease-out`;
                        clone.style.opacity = '0';
                    }
                    if (overlay) {
                        overlay.style.transition = `opacity ${FADE_MS}ms ease-out`;
                        overlay.style.opacity = '0';
                    }
                    setTimeout(() => {
                        if (clone) clone.style.display = 'none';
                        if (overlay) overlay.style.display = 'none';
                        phaseRef.current = 'idle';
                        setMedia(null);
                    }, FADE_MS);
                });
                });
            }, 300);
        }, EXPAND_MS);
    }, [navigate]);

    const runCollapse = useCallback((rect, mediaData, targetUrl) => {
        const clone = cloneRef.current;
        const overlay = overlayRef.current;
        if (!clone || !overlay) return;

        const hero = getHeroRect();

        // Instant: place at hero position
        clone.style.transition = 'none';
        clone.style.opacity = '1';
        clone.style.display = 'block';
        overlay.style.transition = 'none';
        overlay.style.opacity = '1';
        overlay.style.display = 'block';
        applyRect(clone, hero, '0px');

        void clone.offsetHeight;

        // Navigate home so it renders underneath
        const scrollY = parseInt(sessionStorage.getItem('homeScrollY') || '0', 10);
        navigate(targetUrl);

        requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);

            requestAnimationFrame(() => {
                // Animate to card position
                clone.style.transition = `top ${COLLAPSE_MS}ms ${EASE}, left ${COLLAPSE_MS}ms ${EASE}, width ${COLLAPSE_MS}ms ${EASE}, height ${COLLAPSE_MS}ms ${EASE}, border-radius ${COLLAPSE_MS}ms ${EASE}`;
                applyRect(clone, rect, '20px');
                overlay.style.transition = `opacity ${COLLAPSE_MS}ms ease-in`;
                overlay.style.opacity = '0';

                setTimeout(() => {
                    if (clone) {
                        clone.style.transition = `opacity 180ms ease-out`;
                        clone.style.opacity = '0';
                    }
                    setTimeout(() => {
                        if (clone) clone.style.display = 'none';
                        if (overlay) overlay.style.display = 'none';
                        phaseRef.current = 'idle';
                        setMedia(null);
                    }, 180);
                }, COLLAPSE_MS);
            });
        });
    }, [navigate]);

    const expandCard = useCallback((cardId, rect, mediaData, targetUrl) => {
        if (phaseRef.current !== 'idle') return;
        phaseRef.current = 'expanding';

        cardRectsRef.current[cardId] = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
        sessionStorage.setItem('cardTransition', JSON.stringify({ cardId, media: mediaData }));
        sessionStorage.setItem('homeScrollY', String(window.scrollY));
        mediaCache.current = mediaData;

        setMedia({ data: mediaData, callback: () => runExpand(rect, mediaData, targetUrl) });
    }, [runExpand]);

    const collapseCard = useCallback((cardId, targetUrl = '/') => {
        if (phaseRef.current !== 'idle') return;
        phaseRef.current = 'collapsing';

        const stored = cardRectsRef.current[cardId];
        const sessionData = JSON.parse(sessionStorage.getItem('cardTransition') || '{}');
        const rect = stored || {
            top: window.innerHeight / 2 - 150,
            left: window.innerWidth / 2 - 150,
            width: 300,
            height: 300,
        };
        const mediaData = sessionData.media || mediaCache.current || {};

        setMedia({ data: mediaData, callback: () => runCollapse(rect, mediaData, targetUrl) });
    }, [runCollapse]);

    React.useEffect(() => {
        if (media?.callback) {
            requestAnimationFrame(() => media.callback());
        }
    }, [media]);

    const signalReady = useCallback(() => {}, []);

    return (
        <CardTransitionContext.Provider value={{ expandCard, collapseCard, signalReady }}>
            {children}

            <div
                ref={overlayRef}
                className="card-transition-overlay"
                style={{ display: 'none' }}
            />
            <div
                ref={cloneRef}
                className="card-transition-clone"
                style={{ display: 'none' }}
            >
                {media?.data?.type === 'video' ? (
                    <video
                        className="card-transition-media"
                        autoPlay
                        muted
                        loop
                        playsInline
                        src={media.data.src}
                    />
                ) : media?.data?.src ? (
                    <img
                        className="card-transition-media"
                        src={media.data.src}
                        alt=""
                    />
                ) : null}
                {media && <div className="card-transition-gradient" />}
            </div>
        </CardTransitionContext.Provider>
    );
};
